// /recaps/:slug — public detail page for a published exchange recap.
//
// Anonymous-OK. Wrapped in MarketingShell because recaps are editorial content.
// 404 surfaces as an empty-state with a path back to the recaps grid.
//
// Photos render as <img> per CLAUDE.md (no CSS background-image, no
// backdrop-filter). The first photo is treated as above-the-fold and gets
// fetchpriority="high"; the rest are lazy.

import { Anchor, Box, Container, Group, Loader, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { MarketingShell } from '../../components/marketing/MarketingShell';
import { EmptyState, MotionSection } from '../../components/ui';
import { ApiError, fetchOk } from '../../lib/api';

export const Route = createFileRoute('/recaps/$slug')({
  component: RecapDetail,
});

type DetailResponse = {
  recap: {
    id: string;
    slug: string;
    title: string | null;
    body: string;
    publishedAt: string | null;
  };
  exchange: {
    id: string;
    startDate: string;
    endDate: string;
  };
  venue: {
    id: string;
    slug: string;
    name: string;
    city: string;
    country: string;
  } | null;
  artist: {
    userId: string;
    slug: string | null;
    displayName: string;
    city: string | null;
    country: string | null;
  } | null;
  photos: Array<{
    id: string;
    url: string;
    caption: string | null;
    widthPx: number | null;
    heightPx: number | null;
  }>;
};

function RecapDetail() {
  const { slug } = Route.useParams();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchOk(`/api/recaps/public/slug/${encodeURIComponent(slug)}`);
        const body = (await res.json()) as DetailResponse;
        if (cancelled) return;
        setData(body);
        setErrMsg(null);
        setStatus(null);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setStatus(e.status);
          setErrMsg(e.message);
        } else {
          setErrMsg('Network problem loading the recap.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (errMsg !== null) {
    return (
      <MarketingShell>
        <Container size="md" py="xl">
          <EmptyState
            title={status === 404 ? "We couldn't find that recap." : 'Something went sideways.'}
            body={
              status === 404
                ? 'The link may be wrong, or the recap may have been taken down.'
                : errMsg
            }
            action={
              <Anchor component={Link} to="/recaps">
                ← Back to all recaps
              </Anchor>
            }
          />
        </Container>
      </MarketingShell>
    );
  }

  if (data === null) {
    return (
      <MarketingShell>
        <Container size="md" py="xl">
          <Group justify="center">
            <Loader />
          </Group>
        </Container>
      </MarketingShell>
    );
  }

  const fallbackTitle =
    data.artist !== null && data.venue !== null
      ? `${data.artist.displayName} at ${data.venue.name}`
      : data.venue?.name ?? data.artist?.displayName ?? 'A night on the road';
  const titleText =
    data.recap.title !== null && data.recap.title.length > 0 ? data.recap.title : fallbackTitle;

  const dateLine = formatDateRange(data.exchange.startDate, data.exchange.endDate);
  const venueLine =
    data.venue !== null
      ? `${data.venue.name} · ${data.venue.city}, ${data.venue.country}`
      : null;

  const heroPhoto = data.photos[0] ?? null;
  const restPhotos = data.photos.slice(1);

  // Body — render plain paragraphs split on blank lines. We deliberately
  // don't lean on a markdown renderer here; the recap composer is plain
  // textarea and the public surface should match the writer's formatting
  // intent without surprise transformations.
  const paragraphs = data.recap.body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  return (
    <MarketingShell>
      <Container size="md" py="xl">
        <Stack gap="xl">
          <MotionSection>
            <Stack gap="xs">
              <Anchor component={Link} to="/recaps" size="sm">
                ← All recaps
              </Anchor>
              <Title
                order={1}
                style={{
                  fontSize: 'clamp(1.75rem, 4vw, 2.6rem)',
                  lineHeight: 1.15,
                  letterSpacing: '-0.015em',
                  margin: 0,
                }}
              >
                {titleText}
              </Title>
              <Group gap="xs" c="dimmed">
                {dateLine !== null ? <Text size="sm">{dateLine}</Text> : null}
                {dateLine !== null && venueLine !== null ? (
                  <Text size="sm">·</Text>
                ) : null}
                {venueLine !== null ? <Text size="sm">{venueLine}</Text> : null}
              </Group>
            </Stack>
          </MotionSection>

          {heroPhoto !== null ? (
            <MotionSection>
              <Box
                style={{
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: 'var(--mantine-color-brand-0)',
                }}
              >
                <Box
                  component="img"
                  src={heroPhoto.url}
                  alt={heroPhoto.caption ?? titleText}
                  // @ts-expect-error - fetchpriority is a valid React 19 prop, not yet typed
                  fetchpriority="high"
                  decoding="async"
                  style={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 500,
                    objectFit: 'cover',
                  }}
                />
                {heroPhoto.caption !== null && heroPhoto.caption.length > 0 ? (
                  <Text size="xs" c="dimmed" px="md" py="sm">
                    {heroPhoto.caption}
                  </Text>
                ) : null}
              </Box>
            </MotionSection>
          ) : null}

          <MotionSection>
            <Stack gap="md">
              {paragraphs.length === 0 ? (
                <Text c="dimmed">(This recap has no body text.)</Text>
              ) : (
                paragraphs.map((p, i) => (
                  <Text key={i} style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {p}
                  </Text>
                ))
              )}
            </Stack>
          </MotionSection>

          {restPhotos.length > 0 ? (
            <MotionSection>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {restPhotos.map((p, idx) => (
                  <Box
                    key={p.id}
                    style={{
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: 'var(--mantine-color-brand-0)',
                    }}
                  >
                    <img
                      src={p.url}
                      alt={p.caption ?? `Recap photo ${idx + 2}`}
                      loading="lazy"
                      decoding="async"
                      style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        objectFit: 'cover',
                      }}
                    />
                    {p.caption !== null && p.caption.length > 0 ? (
                      <Text size="xs" c="dimmed" px="sm" py={6}>
                        {p.caption}
                      </Text>
                    ) : null}
                  </Box>
                ))}
              </SimpleGrid>
            </MotionSection>
          ) : null}

          <MotionSection>
            <Group gap="md" wrap="wrap">
              {data.artist !== null && data.artist.slug !== null ? (
                <Link
                  to="/artists/$slug"
                  params={{ slug: data.artist.slug }}
                  style={{ textDecoration: 'none' }}
                >
                  <Anchor component="span">{data.artist.displayName} →</Anchor>
                </Link>
              ) : data.artist !== null ? (
                <Text size="sm">{data.artist.displayName}</Text>
              ) : null}
              {data.venue !== null ? (
                <Link
                  to="/venues/$slug"
                  params={{ slug: data.venue.slug }}
                  style={{ textDecoration: 'none' }}
                >
                  <Anchor component="span">{data.venue.name} →</Anchor>
                </Link>
              ) : null}
            </Group>
          </MotionSection>
        </Stack>
      </Container>
    </MarketingShell>
  );
}

function formatDateRange(startIso: string, endIso: string): string | null {
  // startIso / endIso are YYYY-MM-DD calendar strings.
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (start === null || end === null) return null;

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  const fmt = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  if (sameDay) return fmt.format(start);
  return `${fmt.format(start)} → ${fmt.format(end)}`;
}

function parseIsoDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m === null) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return new Date(y, mo - 1, d);
}
